import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaGift, FaUserPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';

const AuthNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Check if user is logged in
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      const isAuthenticated = !!token;
      setIsLoggedIn(isAuthenticated);
      
      // Show notification if not logged in and user hasn't dismissed it
      if (!isAuthenticated && !userDismissed) {
        setIsVisible(true);
      }
    };

    // Check immediately
    checkAuthStatus();

    // Check every 1 minute (60000 ms)
    const interval = setInterval(checkAuthStatus, 60000);

    return () => clearInterval(interval);
  }, [userDismissed]);

  // Auto-hide notification after 15 seconds
  useEffect(() => {
    if (isVisible && !isLoggedIn) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, isLoggedIn]);

  const handleClose = () => {
    setIsVisible(false);
    setUserDismissed(true);
  };

  const handleAuth = () => {
    navigate('/auth');
    setIsVisible(false);
  };

  // Don't show if user is logged in or has dismissed the notification
  if (isLoggedIn || userDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.8 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="fixed bottom-20 right-2 sm:right-4 z-[1000] max-w-xs sm:max-w-sm w-full mx-2 sm:mx-4"
        >
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-2xl p-4 sm:p-6 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-white bg-opacity-10 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white bg-opacity-10 rounded-full translate-y-8 -translate-x-8"></div>
            
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-2 right-2 p-3 text-white hover:text-gray-200 transition-colors rounded-full hover:bg-white hover:bg-opacity-20 z-20 touch-manipulation"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <FaTimes className="text-2xl sm:text-3xl" />
            </button>

            {/* Content */}
            <div className="relative z-10">
              {/* Icon */}
              <div className="flex items-center mb-3 sm:mb-4">
                <div className="bg-white bg-opacity-20 rounded-full p-2 sm:p-3 mr-2 sm:mr-3">
                  <FaGift className="text-lg sm:text-2xl" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold">{t('getFreePrashad')}</h3>
                  <p className="text-xs sm:text-sm text-orange-100">{t('signupToReceiveBlessings')}</p>
                </div>
              </div>

              {/* Message */}
              <p className="text-xs sm:text-sm mb-4 sm:mb-6 text-orange-100 leading-relaxed">
                {t('joinSpiritualCommunity')}
              </p>

              {/* Action button */}
              <div className="flex justify-center">
                <button
                  onClick={handleAuth}
                  className="w-full bg-white text-orange-600 px-4 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center text-sm"
                >
                  <FaUserPlus className="mr-2 text-sm" />
                  {t('signupLogin')}
                </button>
              </div>

              {/* Auto-hide indicator */}
              <div className="mt-4 text-xs text-orange-200 text-center">
                {t('notificationAutoHide')}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthNotification;
